import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Engineering as EngineeringIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  LinkOff as LinkOffIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Stato live macchina Bionen (nodo StatoMacchina): 1=STOP, 2=RESET, 3=START.
// Stesso significato mostrato nel dettaglio macchina.
const BIONEN_STATUS = {
  1: { label: 'STOP', color: 'warning', icon: <StopIcon /> },
  2: { label: 'RESET', color: 'info', icon: <BuildIcon /> },
  3: { label: 'START', color: 'success', icon: <CheckCircleIcon /> }
};

const MachineStatusGrid = ({ machines }) => {
  const navigate = useNavigate();

  const getStatusConfig = (machine) => {
    if (machine.edge_gateway_uuid) {
      // Gateway irraggiungibile
      if (!machine.gateway_online) {
        return { label: 'Gateway Offline', color: 'error', icon: <ErrorIcon /> };
      }
      // Gateway online ma nessun dato live dalla macchina (>10 min): non connessa
      if (machine.machine_link_stale) {
        return { label: 'Macchina Non Connessa', color: 'error', icon: <LinkOffIcon /> };
      }
      // Connessa: mostra lo STATO LIVE della macchina (StatoMacchina), come nel dettaglio,
      // invece del flag di connessione active/inactive che oscilla (flap).
      const sm = parseInt(machine.stato_macchina, 10);
      if (BIONEN_STATUS[sm]) return BIONEN_STATUS[sm];
      if (!isNaN(sm)) return { label: `Errore (${sm})`, color: 'error', icon: <ErrorIcon /> };
      // Connessa ma StatoMacchina non ancora disponibile
      return { label: 'Connessa', color: 'success', icon: <CheckCircleIcon /> };
    }

    // Nessun gateway - stato semplice da configurazione
    const configs = {
      'active': { label: 'Attiva', color: 'success', icon: <CheckCircleIcon /> },
      'inactive': { label: 'Inattiva', color: 'default', icon: null },
      'maintenance': { label: 'Manutenzione', color: 'warning', icon: <BuildIcon /> },
      'error': { label: 'Errore', color: 'error', icon: <ErrorIcon /> }
    };
    return configs[machine.status] || configs['inactive'];
  };

  if (!machines || machines.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <EngineeringIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Nessuna macchina censita
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vai su Macchine per aggiungere la prima macchina
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {machines.map((machine) => {
        const statusConfig = getStatusConfig(machine);
        const machineConnected = machine.gateway_online && !machine.machine_link_stale;

        return (
          <Grid item xs={12} key={machine.id}>
            {/* Card orizzontale a tutta larghezza */}
            <Card
              sx={{
                '&:hover': { boxShadow: 6 },
                transition: 'box-shadow 0.3s ease'
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>

                {/* Identita' macchina + connessione */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 240 }}>
                  <EngineeringIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{machine.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {machine.code}{machine.location ? ` · ${machine.location}` : ''}
                    </Typography>
                    {machine.gateway_name && (
                      <Typography variant="caption" color={machine.gateway_online ? 'success.main' : 'error.main'} sx={{ display: 'block' }}>
                        Gateway: {machine.gateway_name} ({machine.gateway_online ? 'Online' : 'Offline'})
                      </Typography>
                    )}
                    {machine.edge_gateway_uuid && (
                      <Typography variant="caption" color={machineConnected ? 'success.main' : 'error.main'} sx={{ display: 'block', fontWeight: 600 }}>
                        Macchina: {machineConnected ? '● Connessa' : '● Non connessa'}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Stato live (StatoMacchina) */}
                <Box sx={{ minWidth: 150, textAlign: 'center' }}>
                  <Chip label={statusConfig.label} color={statusConfig.color} icon={statusConfig.icon} />
                  {machine.edge_gateway_uuid && machine.gateway_online && machine.machine_link_stale && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      Ultimo dato: {machine.last_machine_data_at ? new Date(machine.last_machine_data_at).toLocaleString('it-IT') : 'mai'}
                    </Typography>
                  )}
                </Box>

                {/* Statistiche in riga (riempiono la larghezza) */}
                <Box sx={{ display: 'flex', gap: 4, flexGrow: 1, flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ordini in produzione</Typography>
                    <Typography variant="h6" fontWeight="bold">{machine.orders_in_production || 0}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ordini attivi</Typography>
                    <Typography variant="h6" fontWeight="bold">{machine.active_orders || 0}</Typography>
                  </Box>
                  {machine.max_speed ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Velocità max</Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {machine.max_speed} <Typography variant="caption" component="span" color="text.secondary">pz/min</Typography>
                      </Typography>
                    </Box>
                  ) : null}
                </Box>

                {/* Azione */}
                <Box>
                  <Tooltip title="Valori live OPC UA">
                    <IconButton size="small" onClick={() => navigate(`/machines/${machine.id}`)} color="secondary">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MachineStatusGrid;
